import React, { ChangeEvent, useEffect } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'motion/react';
import './Checkbox.css';

export interface CheckboxProps {
  label: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
}

export const Checkbox = React.memo(function Checkbox({
  label = 'Value',
  value = 'value',
  required = false,
  disabled = false,
  checked,
  onChange,
  ariaLabel,
}: CheckboxProps) {
  const pathLength = useMotionValue(checked ? 1 : 0);
  const strokeLinecap = useTransform(pathLength, [0, 0.0001, 1], ['none', 'round', 'round']);
  // Tie background and border color to the animated path length so uncheck reverses smoothly
  const backgroundColor = useTransform(
    pathLength,
    [0, 0.001, 1],
    [
      'var(--color-alias-neutral-lightest)',
      'var(--color-alias-primary)',
      'var(--color-alias-primary)',
    ],
  );
  const borderColor = useTransform(
    pathLength,
    [0, 0.001, 1],
    [
      'var(--color-alias-neutral-darker)',
      'var(--color-alias-primary)',
      'var(--color-alias-primary)',
    ],
  );

  useEffect(() => {
    const controls = animate(pathLength, checked ? 1 : 0, {
      type: 'spring',
      bounce: 0,
      duration: 0.3,
    });
    return () => controls.stop();
  }, [checked, pathLength]);

  return (
    <div className="checkbox" data-disabled={disabled} aria-label={ariaLabel}>
      <label>
        <input
          type="checkbox"
          value={value}
          required={required}
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          aria-label={ariaLabel}
        />
        <motion.span
          className="checkbox__mark"
          whileHover={!disabled ? { scale: 1.05 } : undefined}
          whileTap={!disabled ? { scale: 0.95 } : undefined}
          style={{
            willChange: 'transform, background-color, border-color',
            backgroundColor: disabled ? 'var(--color-alias-neutral)' : backgroundColor,
            borderColor: disabled ? 'var(--color-alias-neutral)' : borderColor,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <motion.path d="M4 12L10 18L20 6" style={{ pathLength, strokeLinecap }} />
          </svg>
        </motion.span>
        {label && <span className="checkbox__label">{label}</span>}
      </label>
    </div>
  );
});
